import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type DragEvent,
  type KeyboardEvent,
} from "react";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import {
  Box,
  CircularProgress,
  FormHelperText,
  FormLabel,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  BENCHMARK_IMAGE_ACCEPT,
  BENCHMARK_IMAGE_HEIGHT,
  BENCHMARK_IMAGE_WIDTH,
  validateBenchmarkImageFile,
} from "./benchmarkImage";

type BenchmarkImageFieldProps = {
  savedImageUrl: string | null;
  file: File | null;
  disabled?: boolean;
  isUploading?: boolean;
  onSelect: (file: File) => void;
  onRemove: () => void;
};

const imageFrameSx = {
  position: "relative",
  width: "100%",
  aspectRatio: "16 / 9",
  borderRadius: 1,
  overflow: "hidden",
} as const;

const imageActionSx = {
  bgcolor: "background.paper",
  border: 1,
  borderColor: "divider",
  "&:hover": { bgcolor: "background.paper" },
} as const;

export function BenchmarkImageField({
  savedImageUrl,
  file,
  disabled = false,
  isUploading = false,
  onSelect,
  onRemove,
}: BenchmarkImageFieldProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const validationRequestRef = useRef(0);
  const [error, setError] = useState<string | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  const [isDragActive, setIsDragActive] = useState(false);

  const fileObjectUrl = useMemo(
    () => (file ? URL.createObjectURL(file) : null),
    [file],
  );
  useEffect(
    () => () => {
      if (fileObjectUrl) {
        URL.revokeObjectURL(fileObjectUrl);
      }
    },
    [fileObjectUrl],
  );

  const previewUrl = fileObjectUrl ?? savedImageUrl;
  const isBusy = disabled || isValidating;
  const sizeLabel = `${BENCHMARK_IMAGE_WIDTH}×${BENCHMARK_IMAGE_HEIGHT}`;

  const openPicker = () => {
    if (!isBusy) {
      inputRef.current?.click();
    }
  };

  const handleFile = async (candidate: File | undefined) => {
    if (!candidate || isBusy) {
      return;
    }
    const requestId = ++validationRequestRef.current;
    setIsValidating(true);
    const validationError = await validateBenchmarkImageFile(candidate);
    if (requestId !== validationRequestRef.current) {
      return;
    }
    setIsValidating(false);
    setError(validationError);
    if (!validationError) {
      onSelect(candidate);
    }
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const [candidate] = Array.from(event.target.files ?? []);
    event.target.value = "";
    void handleFile(candidate);
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragActive(false);
    void handleFile(event.dataTransfer.files?.[0]);
  };

  const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (!isBusy) {
      setIsDragActive(true);
    }
  };

  const handleDropzoneKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      openPicker();
    }
  };

  const handleRemove = () => {
    setError(null);
    onRemove();
  };

  return (
    <Stack spacing={0.5}>
      <FormLabel id="benchmark-image-label" sx={{ fontSize: 12 }}>
        Benchmark Image
      </FormLabel>
      <input
        ref={inputRef}
        hidden
        type="file"
        accept={BENCHMARK_IMAGE_ACCEPT}
        aria-label="Benchmark image file"
        onChange={handleInputChange}
      />
      {previewUrl ? (
        <Box
          sx={{ ...imageFrameSx, border: 1, borderColor: "divider" }}
          onDragOver={handleDragOver}
          onDragLeave={() => setIsDragActive(false)}
          onDrop={handleDrop}
        >
          <Box
            component="img"
            src={previewUrl}
            alt="Benchmark image preview"
            sx={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              display: "block",
            }}
          />
          <Stack
            direction="row"
            spacing={1}
            sx={{ position: "absolute", top: 8, right: 8 }}
          >
            <Tooltip title="Replace image">
              <span>
                <IconButton
                  size="small"
                  aria-label="Replace image"
                  onClick={openPicker}
                  disabled={isBusy}
                  sx={imageActionSx}
                >
                  <SwapHorizIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Remove image">
              <span>
                <IconButton
                  size="small"
                  aria-label="Remove image"
                  onClick={handleRemove}
                  disabled={isBusy}
                  sx={{ ...imageActionSx, color: "error.main" }}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
          {isUploading || isValidating || isDragActive ? (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                bgcolor: "rgba(255, 255, 255, 0.7)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {isDragActive ? (
                <Typography variant="body2">Drop to replace</Typography>
              ) : (
                <CircularProgress size={28} aria-label="Processing image" />
              )}
            </Box>
          ) : null}
        </Box>
      ) : (
        <Box
          role="button"
          tabIndex={isBusy ? -1 : 0}
          aria-labelledby="benchmark-image-label"
          aria-describedby="benchmark-image-hint"
          aria-disabled={isBusy}
          onClick={openPicker}
          onKeyDown={handleDropzoneKeyDown}
          onDragOver={handleDragOver}
          onDragLeave={() => setIsDragActive(false)}
          onDrop={handleDrop}
          sx={{
            ...imageFrameSx,
            border: "1px dashed",
            borderColor: isDragActive ? "primary.main" : "text.disabled",
            bgcolor: isDragActive ? "action.hover" : "background.paper",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 0.5,
            textAlign: "center",
            px: 2,
            cursor: isBusy ? "default" : "pointer",
            opacity: disabled ? 0.6 : 1,
            "&:focus-visible": {
              outline: "2px solid",
              outlineColor: "primary.main",
              outlineOffset: 2,
            },
          }}
        >
          {isValidating ? (
            <CircularProgress size={28} aria-label="Processing image" />
          ) : (
            <FileUploadOutlinedIcon color="action" />
          )}
          <Typography variant="body2">
            Drop image or{" "}
            <Box component="span" sx={{ color: "primary.main" }}>
              browse
            </Box>
          </Typography>
          <Typography
            id="benchmark-image-hint"
            variant="caption"
            color="text.secondary"
          >
            PNG or JPG · {sizeLabel}
          </Typography>
        </Box>
      )}
      {error ? (
        <FormHelperText error>{error}</FormHelperText>
      ) : (
        <FormHelperText>
          {file
            ? `${file.name} · ${sizeLabel}`
            : previewUrl
              ? "Current image"
              : "Optional. Shown to teachers and students."}
        </FormHelperText>
      )}
    </Stack>
  );
}
